/// <amd-module name="Repos/FilesHubCommon/Annotate" />
import { IAnnotationBlock, IAnnotationHistoryEntry } from "Repos/Common/Contracts/Editor";
import { IGitItem } from "Repos/Files/Contracts";
import { IReadonlyObservableValue, ReadyableObservableArray } from "VSS/Core/Observable";

const annotateBatchSize = 25;

export interface IAnnotateBatchResult {
    diffs: IAnnotateResult[];
    mObjectId: string;
}

interface IAnnotateResult {
    diff: IFileDiff;
    oObjectId: string | undefined;
}

export interface IFileDiff {
    blocks: IFileDiffBlock[];
    lineCharBlocks: IFileCharDiffBlock[];
}

export interface IFileDiffBlock {
    changeType: FileDiffBlockChangeType;
    mLine: number;
    mLines: string[];
    mLinesCount: number;
    oLine: number;
    oLines: string[];
    oLinesCount: number;
    truncatedAfter: boolean;
    truncatedBefore: boolean;
}

interface IFileCharDiffBlock {
    charChange?: IFileDiffBlock[];
    lineChange: IFileDiffBlock;
}

interface IInternalAnnotateContext {
    blocks: ReadyableObservableArray<IAnnotationBlock>;
    history: IAnnotationHistoryEntry[];
    getFileDiffs: (skip: number, top: number) => Promise<IAnnotateBatchResult[] | undefined>;
    lineMaps: { [objectId: string]: number[] | undefined };
    path: string;
    processedLinesCount: number;
    selectedItem: IReadonlyObservableValue<IGitItem>;
    singleLineAnnotations: Array<{ historyEntry: IAnnotationHistoryEntry; historyIndex: number; oLine: number } | undefined>;
    totalLines: number;
    unprocessedBatchResults: { [historyIndex: number]: IAnnotateBatchResult };
}

/**
 * Type of change for a diff block
 */
const enum FileDiffBlockChangeType {
    /**
     * No change - left and right sides are identical
     */
    None = 0,
    /**
     * Content was added to the right side
     */
    Add = 1,
    /**
     * Content was deleted from the left side
     */
    Delete = 2,
    /**
     * Lines were modified
     */
    Edit = 3
}

export function loadAnnotationBlocks(
    selectedItem: IReadonlyObservableValue<IGitItem>,
    blocks: ReadyableObservableArray<IAnnotationBlock>,
    history: IAnnotationHistoryEntry[],
    getFileDiffs: (skip: number, top: number) => Promise<IAnnotateBatchResult[] | undefined>
) {
    const context: IInternalAnnotateContext = {
        blocks,
        history,
        getFileDiffs,
        lineMaps: {},
        path: selectedItem.value.path,
        processedLinesCount: 0,
        selectedItem,
        singleLineAnnotations: [],
        totalLines: 0,
        unprocessedBatchResults: {}
    };

    fetchAnnotationBlocks(context, 0);
}

function fetchAnnotationBlocks(context: IInternalAnnotateContext, historyBatchIndex: number): void {
    context.getFileDiffs(historyBatchIndex, annotateBatchSize).then(result => {
        // Make sure we are still on the same file
        if (result && context.selectedItem.value.path === context.path) {
            processAnnotationBlocks(context, result, historyBatchIndex);
            if (context.history.length > historyBatchIndex + annotateBatchSize && !context.blocks.ready.value) {
                fetchAnnotationBlocks(context, historyBatchIndex + annotateBatchSize);
            } else {
                context.blocks.ready.value = true;
            }
        }
    });
}

function processAnnotationBlocks(context: IInternalAnnotateContext, annotations: IAnnotateBatchResult[], historyBatchIndex: number): void {
    let isComplete = false;

    for (let i = 0; i < annotations.length; i++) {
        processAnnotationBlock(context, annotations[i], context.history[historyBatchIndex + i], historyBatchIndex + i);
        if (context.processedLinesCount === context.totalLines) {
            isComplete = true;
            break;
        }
    }

    // Process previously unprocessed diff results
    for (const unProcessedResultIndexKey in context.unprocessedBatchResults) {
        const unProcessedResultIndex = Number(unProcessedResultIndexKey);
        processAnnotationBlock(
            context,
            context.unprocessedBatchResults[unProcessedResultIndex],
            context.history[unProcessedResultIndex],
            unProcessedResultIndex
        );
    }

    // Set annotation blocks
    const blocks: IAnnotationBlock[] = [];
    let lastBlock: IAnnotationBlock | undefined;

    for (let lineNumber = 1; lineNumber <= context.totalLines; lineNumber++) {
        const annotation = context.singleLineAnnotations[lineNumber];
        if (lastBlock && lastBlock.historyEntry === (annotation ? annotation.historyEntry : undefined)) {
            lastBlock.lineCount++;
        } else {
            lastBlock = {
                startLine: lineNumber,
                lineCount: 1,
                historyEntry: annotation ? annotation.historyEntry : undefined
            };
            blocks.push(lastBlock);
        }
    }

    context.blocks.value = blocks;
    if (isComplete) {
        context.blocks.ready.value = true;
    }
}

function processAnnotationBlock(
    context: IInternalAnnotateContext,
    annotation: IAnnotateBatchResult,
    historyEntry: IAnnotationHistoryEntry,
    historyIndex: number
): void {
    const lineMap = context.lineMaps[annotation.mObjectId];
    if (historyIndex === 0) {
        // Calculate the total number of lines from the first diff block
        context.totalLines = 0;
        for (let i = annotation.diffs[0].diff.blocks.length - 1; i >= 0; i--) {
            const block = annotation.diffs[0].diff.blocks[i];
            if (block.mLinesCount > 0) {
                context.totalLines = block.mLine + block.mLinesCount - 1;
                break;
            }
        }
    } else if (!lineMap) {
        // No line map for this line. This can happen in cases where 2
        // different branches separately ended up with the same contents.
        // The first instance of this content will "win" by us continuing
        // the annotate down that path.
        context.unprocessedBatchResults[historyIndex] = annotation;
        return;
    }

    // Clear unprocessed batch results (if any) for this entry
    delete context.unprocessedBatchResults[historyIndex];

    if (annotation.diffs.length === 1) {
        // Simple/non-merge result - only one parent. Just update the lines added in this diff block
        updateAnnotateVersionsByLine(context, annotation.diffs[0].diff.blocks, historyEntry, historyIndex, lineMap);
    } else {
        // Merge commit. Only attribute this merge commit to lines that are different from all parents
        updateAnnotateMergeCommitLines(context, annotation.diffs, historyEntry, historyIndex, lineMap);
    }

    // Update the line map which maps lines in the older version to uncalculated lines in the latest version
    for (const annotateDiff of annotation.diffs) {
        if (annotateDiff.oObjectId) {
            const newLineMap: number[] = [];
            for (const block of annotateDiff.diff.blocks) {
                if (block.changeType === FileDiffBlockChangeType.None) {
                    for (let blockLineIndex = 0; blockLineIndex < block.mLinesCount; blockLineIndex++) {
                        if (lineMap) {
                            const latestLineNumber = lineMap[block.mLine + blockLineIndex];
                            if (latestLineNumber && !context.singleLineAnnotations[latestLineNumber]) {
                                newLineMap[block.oLine + blockLineIndex] = latestLineNumber;
                            }
                        } else {
                            newLineMap[block.oLine + blockLineIndex] = block.mLine + blockLineIndex;
                        }
                    }
                }
            }
            context.lineMaps[annotateDiff.oObjectId] = newLineMap;
        }
    }

    if (lineMap) {
        // We are done with this line map. Free its memory. As indicated above, we only process identical content once.
        delete context.lineMaps[annotation.mObjectId];
    }
}

function updateAnnotateMergeCommitLines(
    context: IInternalAnnotateContext,
    annotateDiffs: IAnnotateResult[],
    historyEntry: IAnnotationHistoryEntry,
    historyIndex: number,
    lineMap: number[] | undefined
) {
    const lineHitCounts: number[] = [];
    const oLines: number[] = [];

    for (const annotateDiff of annotateDiffs) {
        for (const block of annotateDiff.diff.blocks) {
            if (block.changeType === FileDiffBlockChangeType.Add || block.changeType === FileDiffBlockChangeType.Edit) {
                const endLineNumber = block.mLine + block.mLinesCount - 1;
                let oLine = block.oLine;
                for (let lineNumber = block.mLine; lineNumber <= endLineNumber; lineNumber++) {
                    lineHitCounts[lineNumber] = (lineHitCounts[lineNumber] || 0) + 1;
                    oLines[lineNumber] = oLine++;
                }
            }
        }
    }

    // Update lines that were changed from every parent commit (attribute to this merge)
    for (let lineNumber = 0; lineNumber < lineHitCounts.length; lineNumber++) {
        if (lineHitCounts[lineNumber] === annotateDiffs.length) {
            updateAnnotateVersionByLine(context, lineNumber, historyEntry, historyIndex, lineMap, oLines[lineNumber]);
        }
    }
}

function updateAnnotateVersionsByLine(
    context: IInternalAnnotateContext,
    diffBlocks: IFileDiffBlock[],
    historyEntry: IAnnotationHistoryEntry,
    historyIndex: number,
    lineMap: number[] | undefined
) {
    for (const block of diffBlocks) {
        if (block.changeType === FileDiffBlockChangeType.Add || block.changeType === FileDiffBlockChangeType.Edit) {
            const endLineNumber = block.mLine + block.mLinesCount - 1;

            let oLine = block.oLine;
            for (let lineNumber = block.mLine; lineNumber <= endLineNumber; lineNumber++) {
                updateAnnotateVersionByLine(context, lineNumber, historyEntry, historyIndex, lineMap, oLine++);
            }
        }
    }
}

function updateAnnotateVersionByLine(
    context: IInternalAnnotateContext,
    lineNumber: number,
    historyEntry: IAnnotationHistoryEntry,
    historyIndex: number,
    lineMap: number[] | undefined,
    oLine: number
) {
    const mappedLineNumber = lineMap ? lineMap[lineNumber] : lineNumber;
    if (mappedLineNumber && !context.singleLineAnnotations[mappedLineNumber]) {
        context.singleLineAnnotations[mappedLineNumber] = { historyEntry, historyIndex, oLine };
        context.processedLinesCount++;
    }
}
